import React, { useEffect, useRef, useState } from "react";
import MolGrid, { Column } from "@moltech/molui/molgrid";
import CommonDatePickerFilter from "../../../../../../components/filters/CommonDatePicker.filter";
import CommonDropdownFilter from "../../../../../../components/filters/CommonDropdown.filter";
import Checkbox from "../../../../../../components/ui/inputs/checkBox/CheckBox";
import Buttons from "../../../../../../components/ui/button/Buttons";
import CardSection from "../../../../../../components/ui/card/CardSection";
import { useLazyGetContractContractsWithPriorInvoicesQuery } from "../../../../../../app/services/contractAPI";
import { useGetMolGridDataMutation } from "../../../../../../app/services/commonAPI";

const PriorInvoices = () => {
  const gridRef = useRef();

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [contractList, setContractList] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);

  const [
    getContractContractsWithPriorInvoices,
    {
      isFetching: isContractContractsWithPriorInvoicesFetching,
      isSuccess: isContractContractsWithPriorInvoicesFetched,
      data: ContractContractsWithPriorInvoicesData,
    },
  ] = useLazyGetContractContractsWithPriorInvoicesQuery();

  useEffect(() => {
    const newDate = new Date();
    newDate.setMonth(newDate.getMonth() - 1);
    setStartDate(newDate);
  }, []);

  useEffect(() => {
    getContractContractsWithPriorInvoices({
      pbIncludeNone: true,
      pbIncludeExpired: isCheckboxChecked,
    });
  }, [isCheckboxChecked]);

  useEffect(() => {
    if (
      !isContractContractsWithPriorInvoicesFetching &&
      isContractContractsWithPriorInvoicesFetched &&
      ContractContractsWithPriorInvoicesData
    ) {
      const DropDrownData = ContractContractsWithPriorInvoicesData.map(
        (item) => ({ value: item.lContractKey, label: item.sContractName1 })
      );
      setContractList(DropDrownData);
    }
  }, [
    isContractContractsWithPriorInvoicesFetching,
    isContractContractsWithPriorInvoicesFetched,
    ContractContractsWithPriorInvoicesData,
  ]);

  const handleDatePickerChange = (dataField, selectedDate) => {
    if (dataField === "startDate") {
      setStartDate(selectedDate);
    } else if (dataField === "endDate") {
      setEndDate(selectedDate);
    }
  };

  const handleDropdownChange = (selectedValue) => {
    setSelectedContract(selectedValue);
  };

  const handleRefreshClick = () => {
    gridRef.current.reload();
  };

  const beforeMolGridLoad = (reqPrm) => {
    const req = {
      pagination: {
        pageNumber: reqPrm.first / reqPrm.rows + 1,
        pageSize: reqPrm.rows,
      },
      plContractKey: selectedContract ? selectedContract.value : 0,
      plDepartmentKey: 0,
      plInvoiceKey: 0,
      pdtStartDate: startDate,
      pdtEndDate: endDate,
    };
    return req;
  };

  const afterMolGridLoad = (data) => {
    return {
      data: data.dataSource,
      count: data.totalRecord,
    };
  };

  const getCtxMenuItems = (row) => {
    if (!row) return [];
    return [
      {
        label: "View",
      },
    ];
  };

  return (
    <CardSection>
      <div className="prior-invoice-detail">
        <div className="top-form-section">
          <div className="row">
            <div className="col-md-4 from-to-sec">
              <div className="w-100 d-flex gap-3">
                <CommonDatePickerFilter
                  value={startDate}
                  label={"From : "}
                  placeholder="MM/DD/YYYY"
                  onChange={(selectedDate) =>
                    handleDatePickerChange("startDate", selectedDate)
                  }
                />
                <CommonDatePickerFilter
                  value={endDate}
                  label={"To : "}
                  placeholder="MM/DD/YYYY"
                  onChange={(selectedDate) =>
                    handleDatePickerChange("endDate", selectedDate)
                  }
                />
              </div>
            </div>
            <div className="col-md-4 type-spacing">
              <CommonDropdownFilter
                label={"Contract : "}
                placeholder="Select Contract"
                listOptions={contractList}
                selectedValue={selectedContract}
                onChange={handleDropdownChange}
              />
            </div>
            <div className="col-md-2 check-box-sec d-flex align-items-center">
              <Checkbox
                key="expiredInvoices"
                name="expiredInvoices"
                label="Include Expired"
                checked={isCheckboxChecked}
                onChange={() => setIsCheckboxChecked(!isCheckboxChecked)}
              />
            </div>
            <div className="col-md-2 d-flex justify-content-end align-items-center">
              <div className="btn-sec">
                <Buttons
                  buttonText={"Refresh"}
                  buttonTypeClassName="btn blue-btn"
                  onClick={handleRefreshClick}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="scrollable-grid">
          <MolGrid
            ref={gridRef}
            beforeLoad={beforeMolGridLoad}
            afterLoad={afterMolGridLoad}
            client={useGetMolGridDataMutation}
            url={`/Contract/GetAllContractInvoice`}
            grouping
            expandableRowGroups={true}
            paginator
            rowsPerPageOptions={[10, 25, 50, 100]}
            rows={100}
            scrollable
            scrollHeight="flex"
            filterDisplay="row"
            sortMode="single"
            showGridlines
            resizableColumns
            ctxMenuItems={getCtxMenuItems}
          >
            <Column
              field="lContractKey"
              header="Contract Key"
              bodyStyle={{ textAlign: "center" }}
              style={{ width: "7%" }}
              sortable
            />
            <Column
              field="sContractName1"
              header="Contract"
              bodyStyle={{ textAlign: "left" }}
              style={{ width: "25%" }}
              sortable
            />
            <Column
              field="sClientName1"
              header="Client"
              bodyStyle={{ textAlign: "left" }}
              style={{ width: "20%" }}
              sortable
            />
            <Column
              field="sDepartmentName"
              header="Department"
              bodyStyle={{ textAlign: "center" }}
              style={{ width: "12%" }}
              sortable
            />
            <Column
              field="sPurchaseOrder"
              header="Purchase Order"
              bodyStyle={{ textAlign: "Center" }}
              style={{ width: "10%" }}
              sortable
            />
            <Column
              field="dtTranDate"
              header="Invoice Date"
              bodyStyle={{ textAlign: "Center" }}
              style={{ width: "7%" }}
              colType="datetime"
              format="mm/dd/yyyy"
              sortable
            />
            <Column
              field="sTranNumber"
              header="Invoice Number"
              bodyStyle={{ textAlign: "Center" }}
              style={{ width: "7%" }}
              sortable
            />

            <Column
              field="dblTranAmount"
              header="Invoice Amount"
              bodyStyle={{ textAlign: "Right" }}
              style={{ width: "7%" }}
              colType="money"
              sortable
            />
            <Column
              field="sCoveragePeriod"
              header="Coverage Period"
              bodyStyle={{ textAlign: "Center" }}
              style={{ width: "5%" }}
              sortable
            />
          </MolGrid>
        </div>
      </div>
    </CardSection>
  );
};

export default PriorInvoices;
